// Aurora Innovation, Inc. Proprietary and Confidential. Copyright 2026.
function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
import { isDarkMode } from '../../provider/types';
import { blue } from '../primitiveColors/blue';
import { cyan } from '../primitiveColors/cyan';
import { grey } from '../primitiveColors/grey';
import { purple } from '../primitiveColors/purple';
import { transparency } from '../primitiveColors/transparency';
import { background } from './background';
var linear = function(topColor, bottomColor) {
    var angle = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : 180, topPosition = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : 0, bottomPosition = arguments.length > 4 && arguments[4] !== void 0 ? arguments[4] : 100;
    return {
        type: 'linear',
        angle: angle,
        stops: [
            {
                color: topColor,
                position: topPosition
            },
            {
                color: bottomColor,
                position: bottomPosition
            }
        ]
    };
};
var radial = function(transform, stops) {
    return {
        type: 'radial',
        transform: transform,
        stops: stops
    };
};
// A literal `#RRGGBB` inside the SVG data URI below gets parsed as a URL fragment
// separator (truncating everything after it) once the string is used as an actual
// data: URI - not merely a quoted CSS token. Figma's own SVG export sidesteps this by
// writing stop colors as rgb()/rgba() instead of hex; this converts to match.
function hexToRgbFunction(hex) {
    var clean = hex.replace('#', '');
    var r = parseInt(clean.slice(0, 2), 16);
    var g = parseInt(clean.slice(2, 4), 16);
    var b = parseInt(clean.slice(4, 6), 16);
    if (clean.length === 8) {
        var alpha = parseInt(clean.slice(6, 8), 16) / 255;
        return "rgba(".concat(r, ", ").concat(g, ", ").concat(b, ", ").concat(alpha, ")");
    }
    return "rgb(".concat(r, ", ").concat(g, ", ").concat(b, ")");
}
export var toCssGradient = function(g) {
    if (g.type === 'linear') {
        var stops = g.stops.map(function(s) {
            return "".concat(s.color, " ").concat(s.position, "%");
        }).join(', ');
        return "linear-gradient(".concat(g.angle, "deg, ").concat(stops, ")");
    }
    var _g_transform = _sliced_to_array(g.transform, 6), a = _g_transform[0], b = _g_transform[1], c = _g_transform[2], d = _g_transform[3], e = _g_transform[4], f = _g_transform[5];
    var stops1 = g.stops.map(function(s) {
        return "<stop stop-color='".concat(hexToRgbFunction(s.color), "' offset='").concat(s.position, "'/>");
    }).join('');
    var svg = "<svg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none'>" + "<rect x='0' y='0' height='100%' width='100%' fill='url(%23grad)'/>" + "<defs><radialGradient id='grad' gradientUnits='userSpaceOnUse' cx='0' cy='0' r='10' " + "gradientTransform='matrix(".concat(a, " ").concat(b, " ").concat(c, " ").concat(d, " ").concat(e, " ").concat(f, ")'>").concat(stops1, "</radialGradient></defs>") + "</svg>";
    // Matches Figma's own SVG data-URI export exactly: embedded raw (not percent-encoded -
    // encodeURIComponent would double-escape the literal `%23` in `url(%23grad)` above,
    // corrupting the fill reference). Single-quoted SVG attributes avoid clashing with the
    // outer double-quoted CSS url() string.
    // The SVG has no intrinsic width/height (only viewBox), so as a background-image it
    // must be sized explicitly - otherwise browsers tile it at a default intrinsic size
    // instead of filling the element.
    return 'url("data:image/svg+xml;utf8,'.concat(svg, '") center / cover no-repeat');
};
// grayRadial's ellipse shape/rotation is a fixed property of the Figma style, not a
// per-mode variable - only the stop colors below vary between light and dark.
var GRAY_RADIAL_TRANSFORM = [
    0.65625,
    8.0638,
    -8.0638,
    1.5401,
    43.438,
    19.362
];
var lightBg = background('light');
var darkBg = background('dark');
var light = {
    accentLinear: {
        default: linear(cyan[500], blue[500]),
        hover: linear(cyan[500], blue[600]),
        active: linear(cyan[500], blue[700])
    },
    grayLinear: {
        default: linear(grey[200], grey[300]),
        hover: linear(grey[300], grey[400]),
        active: linear(grey[400], grey[500])
    },
    grayRadial: radial(GRAY_RADIAL_TRANSFORM, [
        {
            color: lightBg.level50,
            position: 0
        },
        {
            color: lightBg.level300,
            position: 0.50481
        },
        {
            color: lightBg.level100,
            position: 1
        }
    ]),
    heavyLinear: linear(lightBg.level200, lightBg.level100),
    // Figma is intentional here: `default` uses a diagonal 135deg gradient with inset stops
    // (2.7778%/97.222%) while hover/active are the standard 180deg/0%/100% - not a typo.
    specialLinear: {
        default: linear(purple[900], purple[600], 135, 2.7778, 97.222),
        hover: linear(purple[800], purple[500]),
        active: linear(purple[700], purple[400])
    },
    subtleLinear: linear(lightBg.level0, lightBg.level50),
    transparentLinear: {
        default: linear(transparency.gray.dark[0], transparency.gray.dark[50]),
        hover: linear(transparency.gray.dark[0], transparency.gray.dark[100]),
        active: linear(transparency.gray.dark[0], transparency.gray.dark[200])
    }
};
var dark = {
    accentLinear: {
        default: linear(cyan[500], blue[500]),
        hover: linear(cyan[500], blue[600]),
        active: linear(cyan[500], blue[700])
    },
    grayLinear: {
        default: linear(grey[500], grey[600]),
        hover: linear(grey[400], grey[500]),
        active: linear(grey[300], grey[400])
    },
    grayRadial: radial(GRAY_RADIAL_TRANSFORM, [
        {
            color: darkBg.level50,
            position: 0
        },
        {
            color: darkBg.level0,
            position: 0.50481
        },
        {
            color: darkBg.level100,
            position: 1
        }
    ]),
    heavyLinear: linear(darkBg.level200, darkBg.level100),
    specialLinear: {
        default: linear(purple[900], purple[600], 135, 2.7778, 97.222),
        hover: linear(purple[800], purple[500]),
        active: linear(purple[700], purple[400])
    },
    subtleLinear: linear(darkBg.level0, darkBg.level50),
    transparentLinear: {
        default: linear(transparency.gray.light[0], transparency.gray.light[50]),
        hover: linear(transparency.gray.light[0], transparency.gray.light[100]),
        active: linear(transparency.gray.light[0], transparency.gray.light[200])
    }
};
export var gradient = function(mode) {
    return isDarkMode(mode) ? dark : light;
};

//# sourceMappingURL=gradient.js.map